import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import ChevronIcon from "assets/icons/ChevronIcon";
import { useEffect, useRef, useState } from "react";
import FilterMenu from "views/components/FilterMenu";
import FilterMenuIcon from "assets/icons/FilterMenuIcon";
import {
  getMislabelsThresholdRange,
  getShowQueryPanel,
} from "redux/SingleDataset/selectors";
import { ENTITY_TYPE_PARAMETER } from "helpers/constants/miscellaneous";
import { createAndUpdateIssueTypesFilter } from "helpers/utility/vqlHelpers";
import ErrorModal from "views/modals/ErrorModal";
import useQueryParam from "hooks/useQueryParam";
import useVqlParams from "hooks/useVqlParams";

import styles from "./style.module.scss";
import {
  setSelectedQueryModal,
  setShowQueryPanel,
  clearFilters,
} from "redux/SingleDataset/actions";

import {
  Filters,
  Position,
  QueryOperator,
  isValidEntityType,
  FilterMenuItemType,
} from "types";
import { isCustomMetadata } from "helpers/utility/customMetadataHelpers";

const BTN_HEIGHT_WITH_OFFSET = 44;
type ErrorDetailsWithAction = {
  title: string;
  message: string;
  actionButton: { title: string; onClick: () => void };
};
const AddFilterButton = () => {
  const entityParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityTypeFromQueryParam = isValidEntityType(entityParam)
    ? entityParam
    : null;

  const { vqlQueries, addVqlQuery } = useVqlParams();
  const isFilterApplied = vqlQueries.length > 0;
  const showPanel = useSelector((state) =>
    getShowQueryPanel(state, vqlQueries)
  );
  const [showMenu, setShowMenu] = useState(false);
  const [errorModal, setErrorModal] = useState<
    ErrorDetailsWithAction | undefined
  >();
  const [modalPosition, setModalPosition] = useState<Position | null>(null);
  const btnRef = useRef<HTMLDivElement>(null);
  const { datasetId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const mislabelsRange = useSelector((state) =>
    getMislabelsThresholdRange(state, entityTypeFromQueryParam)
  );

  const title = isFilterApplied ? `${vqlQueries.length}` : "Add Filter";

  const btnClassnames = classNames(styles.addFilterButton, {
    [styles.selected]: showPanel,
  });

  const handleClick = () => {
    if (!isFilterApplied) {
      setShowMenu(!showMenu);
      return;
    }

    dispatch(setShowQueryPanel(!showPanel));
  };

  const defaultErrorModal: ErrorDetailsWithAction = {
    title: "This filter is not available",
    message: "This filter is not available for this dataset",
    actionButton: {
      title: "OK",
      onClick: () => setErrorModal(undefined),
    },
  };

  const customMetadataErrorModal: ErrorDetailsWithAction = {
    title: "No values available for this field",
    message:
      "This field has no values available for this dataset, please add them to use this filter.",
    actionButton: {
      title: "OK",
      onClick: () => setErrorModal(undefined),
    },
  };

  const handleFilterSelection = (
    type: FilterMenuItemType,
    disabled: boolean
  ) => {
    if (disabled) {
      if (isCustomMetadata(type)) {
        setErrorModal(customMetadataErrorModal);
      } else {
        setErrorModal(handleDisabledFilter[type as Filters]);
      }
      return;
    }

    // Handle custom metadata filters
    if (isCustomMetadata(type)) {
      // For now, we'll treat custom metadata filters similar to regular filters
      // You might want to implement specific logic for custom metadata filters here
      if (modalPosition) {
        dispatch(setSelectedQueryModal({ type, position: modalPosition }));
      }
      return;
    }

    //if thresholds donot exist for mislabels, do not open modal
    if (type === Filters.MISLABELS && !mislabelsRange) {
      const vqlQuery = createAndUpdateIssueTypesFilter(
        "mislabels",
        QueryOperator.IS,
        [0, 1]
      );
      addVqlQuery(vqlQuery);
      return;
    }

    if (modalPosition) {
      dispatch(setSelectedQueryModal({ type, position: modalPosition }));
    }
  };

  const handleGotoEnrichment = () => {
    dispatch(clearFilters() as any);
    navigate(`/dataset/${datasetId!}/enrich`);
  };

  const handleDisabledFilter: Record<Filters, ErrorDetailsWithAction> = {
    [Filters.CAPTION]: {
      title: "No captions available for this dataset",
      message:
        "To use search by caption, enrich your dataset using one of our Image-to-Text models. This will generate captions and unlock the caption filter",
      actionButton: {
        title: "Go to Enrichment",
        onClick: handleGotoEnrichment,
      },
    },
    [Filters.DUPLICATES]: {
      title: "Duplicates filter isn't available for this dataset",
      message: "You'll find it in datasets created with newer versions.",
      actionButton: { title: "OK", onClick: () => setErrorModal(undefined) },
    },
    [Filters.FILE]: defaultErrorModal,
    [Filters.LABELS]: defaultErrorModal,
    [Filters.ISSUES]: defaultErrorModal,
    [Filters.FOLDER]: defaultErrorModal,
    [Filters.OUTLIERS]: defaultErrorModal,
    [Filters.LABEL_OUTLIER]: defaultErrorModal,
    [Filters.USER_TAGS]: defaultErrorModal,
    [Filters.MISLABELS]: defaultErrorModal,
    [Filters.UNIQUENESS]: defaultErrorModal,
    [Filters.SIZE]: defaultErrorModal,
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!btnRef.current?.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const top = rect.top + BTN_HEIGHT_WITH_OFFSET; // adjust for modal position
      setModalPosition({ top, left: rect.left });
    }
  }, [showMenu, isFilterApplied]);

  return (
    <div className={styles.btnWrapper} onClick={handleClick} ref={btnRef}>
      <div className={btnClassnames}>
        {isFilterApplied ? (
          <div className={styles.iconWrapper}>
            <span className={styles.floatingTitle}>{title}</span>
            <FilterMenuIcon variant={!isFilterApplied ? "add" : "default"} />
          </div>
        ) : (
          <>
            <FilterMenuIcon variant={!isFilterApplied ? "add" : "default"} />
            <span className={styles.title}>{title}</span>
          </>
        )}
        {isFilterApplied && (
          <ChevronIcon direction={showPanel ? "up" : "down"} />
        )}
      </div>
      {showMenu && !isFilterApplied && (
        <div className={styles.positioner}>
          <FilterMenu onSelect={handleFilterSelection} />
        </div>
      )}

      <ErrorModal
        showModal={!!errorModal}
        title={errorModal?.title || ""}
        message={errorModal?.message || ""}
        onClose={() => setErrorModal(undefined)}
        actionButton1={
          <div
            className={styles.goToEnrichment}
            onClick={errorModal?.actionButton.onClick}
          >
            {errorModal?.actionButton.title}
          </div>
        }
      />
    </div>
  );
};

export default AddFilterButton;
